import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, AlertTriangle, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { HANDED_TO_TYPES, FUEL_LEVELS, inr } from "./lib";

/**
 * Gate Pass creation flow. Prefilled from a linked invoice (via ?invoice=xxx).
 * Enforces payment eligibility client-side and displays a large warning if the
 * user attempts to bypass it (backend re-validates + audits).
 */
export default function GatePassForm() {
  const [sp] = useSearchParams();
  const invId = sp.get("invoice");
  const jcId = sp.get("job_card");
  const nav = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [jc, setJc] = useState(null);
  const [nextNumber, setNextNumber] = useState("");
  const [loading, setLoading] = useState(!!invId || !!jcId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(defaultForm());

  const bootstrap = useCallback(async () => {
    api.get("/billing/gate-passes/next-number").then((r) => setNextNumber(r.data.next_number || ""));
    if (invId) {
      try {
        const r = await api.get(`/billing/invoices/${invId}`);
        setInvoice(r.data);
        if (r.data.job_card_id) {
          const jr = await api.get(`/job-cards/${r.data.job_card_id}`);
          setJc(jr.data);
        }
      } catch (e) { toast.error("Could not load invoice"); nav("/gate-pass"); }
    } else if (jcId) {
      try {
        const r = await api.get(`/job-cards/${jcId}`);
        setJc(r.data);
      } catch (e) { toast.error("Could not load job card"); nav("/gate-pass"); }
    }
    setLoading(false);
  }, [invId, jcId, nav]);
  useEffect(() => { bootstrap(); }, [bootstrap]);

  const balance = invoice?.balance ?? 0;
  const paymentSettled = balance <= 0.01;
  const jcReady = !jc || ["ready_for_delivery", "quality_check", "work_completed"].includes(jc.status);
  const needsOverride = !paymentSettled;

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    if (!form.handed_to_name.trim()) { setError("Receiver name is required"); return; }
    if (needsOverride && !form.override_used) {
      setError("Balance is not settled — enable the override and record a reason.");
      return;
    }
    setSaving(true); setError(null);
    try {
      const payload = {
        invoice_id: invId, job_card_id: jc?.id || null, vehicle_id: invoice?.vehicle_id || jc?.vehicle_id,
        handed_to_type: form.handed_to_type,
        handed_to_name: form.handed_to_name,
        handed_to_phone: form.handed_to_phone,
        handed_to_relation: form.handed_to_relation,
        handed_to_id_ref: form.handed_to_id_ref,
        authorised_by_customer: form.authorised_by_customer,
        odometer_at_exit: form.odometer_at_exit === "" ? null : Number(form.odometer_at_exit),
        fuel_at_exit: form.fuel_at_exit,
        exit_at: form.exit_at || null,
        security_person: form.security_person,
        customer_remarks: form.customer_remarks,
        internal_notes: form.internal_notes,
        checklist: form.checklist,
        signature_data: form.signature_data,
        override_used: form.override_used,
        override_reason: form.override_reason || null,
        override_note: form.override_note,
      };
      const r = await api.post("/billing/gate-passes", payload);
      toast.success(`Gate Pass ${r.data.number} issued`);
      nav(`/gate-pass/${r.data.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create gate pass");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-10 text-center text-[13px] text-[#667085]">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="gate-pass-form-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Link to="/gate-pass" data-testid="gp-form-back"
                className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#667085] hover:bg-[#F1FAEE]">
            <ArrowLeft size={15} />
          </Link>
          <div>
            <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">New Gate Pass</h1>
            <p className="text-[12.5px] text-[#667085] mt-0.5 font-mono">{nextNumber}</p>
          </div>
        </div>
        <button onClick={submit} disabled={saving}
                data-testid="gp-issue-btn"
                className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
          <ShieldCheck size={13} /> {saving ? "Issuing…" : "Issue Gate Pass"}
        </button>
      </header>

      {/* Eligibility banner */}
      {invoice && (
        <section className={`rounded-xl border p-4 ${paymentSettled ? "bg-[#F1FAEE] border-[#12B76A]/40" : "bg-[#FEF0C7] border-[#F79009]/40"}`}
                 data-testid="gp-eligibility-banner">
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-md grid place-items-center ${paymentSettled ? "bg-[#12B76A]/20 text-[#0F9256]" : "bg-[#F79009]/20 text-[#B54708]"}`}>
              {paymentSettled ? <Check size={14} /> : <AlertTriangle size={14} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-bold text-[#1d3557]">
                {paymentSettled ? "Ready to release" : `Balance due ${inr(balance)}`}
              </div>
              <div className="text-[12px] text-[#457b9d] mt-0.5">
                Invoice <Link to={`/invoices/${invoice.id}`} className="font-mono font-semibold text-[#1d3557]">{invoice.number}</Link>
                · Total {inr(invoice.totals?.invoice_total)} · Paid {inr(invoice.paid_amount)}
                {jc && <> · JC <Link to={`/job-cards/${jc.id}`} className="font-mono">{jc.number || jc.job_number}</Link></>}
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Receiver */}
          <Card title="Vehicle handed to" testId="gp-receiver">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Receiver type">
                <select value={form.handed_to_type}
                        onChange={(e) => setF({ handed_to_type: e.target.value })}
                        data-testid="gp-receiver-type"
                        className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
                  {HANDED_TO_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Name *">
                <div className="flex gap-2">
                  <input value={form.handed_to_name}
                         onChange={(e) => setF({ handed_to_name: e.target.value })}
                         data-testid="gp-receiver-name"
                         className="flex-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
                  {(() => {
                    // Prefer the invoice's snapshot (that's what the receiver
                    // sees on the receipt) but fall back to the JC's if the
                    // gate pass was opened straight off a JC.
                    const cust = invoice?.customer_snapshot || jc?.customer_snapshot;
                    if (!cust?.name) return null;
                    const alreadyFilled = form.handed_to_name.trim() === cust.name.trim();
                    return (
                      <button
                        type="button"
                        onClick={() => setF({
                          handed_to_name: cust.name,
                          handed_to_phone: form.handed_to_phone || cust.phone || cust.whatsapp || "",
                          handed_to_relation: "",
                          handed_to_type: "customer",
                        })}
                        disabled={alreadyFilled}
                        data-testid="gp-use-customer"
                        title={`Use customer: ${cust.name}`}
                        className="h-10 px-3 rounded-md border border-[#457b9d] text-[11.5px] font-semibold text-[#457b9d] hover:bg-[#F1FAEE] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {alreadyFilled ? "✓ Customer" : "Use customer"}
                      </button>
                    );
                  })()}
                </div>
              </Field>
              <Field label="Phone">
                <input value={form.handed_to_phone}
                       onChange={(e) => setF({ handed_to_phone: e.target.value })}
                       data-testid="gp-receiver-phone"
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
              </Field>
              <Field label="Relationship / role">
                <input value={form.handed_to_relation}
                       onChange={(e) => setF({ handed_to_relation: e.target.value })}
                       placeholder="e.g. Son, Driver"
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
              </Field>
              <Field label="ID reference (optional)">
                <input value={form.handed_to_id_ref}
                       onChange={(e) => setF({ handed_to_id_ref: e.target.value })}
                       placeholder="DL / Aadhaar last 4"
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
              </Field>
              <label className="inline-flex items-center gap-2 text-[12.5px] text-[#1d3557] mt-6">
                <input type="checkbox" checked={form.authorised_by_customer}
                       onChange={(e) => setF({ authorised_by_customer: e.target.checked })}
                       data-testid="gp-authorised"
                       className="w-4 h-4 accent-[#e63946]" />
                Authorised by customer
              </label>
            </div>
            {form.handed_to_type !== "customer" && (
              <div className="mt-3 rounded-md bg-[#FEF0C7] border border-[#F79009]/40 px-3 py-2 text-[11.5px] text-[#B54708]"
                   data-testid="gp-third-party-warning">
                Vehicle is being released to someone other than the customer. Confirm authorisation.
              </div>
            )}
          </Card>

          {/* Exit details */}
          <Card title="Vehicle exit" testId="gp-exit">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Odometer at exit">
                <input type="number" value={form.odometer_at_exit}
                       onChange={(e) => setF({ odometer_at_exit: e.target.value })}
                       data-testid="gp-odometer"
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
              </Field>
              <Field label="Fuel level at exit">
                <select value={form.fuel_at_exit}
                        onChange={(e) => setF({ fuel_at_exit: e.target.value })}
                        data-testid="gp-fuel"
                        className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
                  <option value="">Not captured</option>
                  {FUEL_LEVELS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
              <Field label="Exit date/time">
                <input type="datetime-local" value={form.exit_at}
                       onChange={(e) => setF({ exit_at: e.target.value })}
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
              </Field>
              <Field label="Security / gate person">
                <input value={form.security_person}
                       onChange={(e) => setF({ security_person: e.target.value })}
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full md:col-span-3" />
              </Field>
            </div>
          </Card>

          {/* Checklist */}
          <Card title="Delivery checklist" testId="gp-checklist">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {CHECKLIST_ITEMS.map((c) => (
                <label key={c.key} className="flex items-center gap-2 text-[12.5px] text-[#1d3557] py-1">
                  <input type="checkbox" checked={!!form.checklist[c.key]}
                         onChange={(e) => setF({ checklist: { ...form.checklist, [c.key]: e.target.checked } })}
                         data-testid={`gp-check-${c.key}`}
                         className="w-4 h-4 accent-[#e63946]" />
                  {c.label}
                </label>
              ))}
            </div>
          </Card>

          {/* Override */}
          {needsOverride && (
            <Card title="Payment override" testId="gp-override">
              <label className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#e63946]">
                <input type="checkbox" checked={form.override_used}
                       onChange={(e) => setF({ override_used: e.target.checked })}
                       data-testid="gp-override-checkbox"
                       className="w-4 h-4 accent-[#e63946]" />
                Release vehicle despite outstanding balance
              </label>
              {form.override_used && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Override reason *">
                    <select value={form.override_reason}
                            onChange={(e) => setF({ override_reason: e.target.value })}
                            data-testid="gp-override-reason"
                            className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
                      <option value="">Choose reason…</option>
                      <option value="approved_credit">Approved credit</option>
                      <option value="partial_payment">Partial payment agreed</option>
                      <option value="owner_override">Owner override</option>
                      <option value="other">Other</option>
                    </select>
                  </Field>
                  <Field label="Approval note">
                    <input value={form.override_note}
                           onChange={(e) => setF({ override_note: e.target.value })}
                           placeholder="e.g. Pay by Friday · approved by owner on call"
                           data-testid="gp-override-note"
                           className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
                  </Field>
                </div>
              )}
              <div className="mt-3 text-[11px] text-[#B54708]">
                Only owners and branch managers can authorise override. Every override is audited.
              </div>
            </Card>
          )}

          {/* Notes */}
          <Card title="Remarks" testId="gp-remarks">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Customer remarks">
                <textarea rows={2} value={form.customer_remarks}
                          onChange={(e) => setF({ customer_remarks: e.target.value })}
                          className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
              </Field>
              <Field label="Internal notes">
                <textarea rows={2} value={form.internal_notes}
                          onChange={(e) => setF({ internal_notes: e.target.value })}
                          className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
              </Field>
            </div>
          </Card>

          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12.5px] text-[#e63946]"
                 data-testid="gp-form-error">
              {error}
            </div>
          )}
        </div>

        {/* Right column — Snapshot */}
        <aside>
          <div className="bg-white rounded-xl border border-[#E4E7EC] p-4 sticky top-4" data-testid="gp-form-summary">
            <h3 className="text-[13px] font-bold text-[#1d3557] mb-2">Snapshot</h3>
            {invoice ? (
              <div className="space-y-2 text-[12px]">
                <SnapRow label="Invoice" value={invoice.number} mono />
                <SnapRow label="Customer" value={invoice.customer_snapshot?.name || "—"} />
                <SnapRow label="Vehicle" value={invoice.vehicle_snapshot?.reg_number || "—"} mono />
                {jc && <SnapRow label="Job Card" value={jc.number || jc.job_number} mono />}
                <SnapRow label="Invoice total" value={inr(invoice.totals?.invoice_total)} mono />
                <SnapRow label="Paid" value={inr(invoice.paid_amount)} mono />
                <SnapRow label="Balance" value={inr(invoice.balance)} mono tone={invoice.balance > 0 ? "red" : "green"} />
                <SnapRow label="Payment status" value={String(invoice.payment_status || "").replace(/_/g, " ").toUpperCase()} />
              </div>
            ) : jc ? (
              <div className="space-y-2 text-[12px]">
                <SnapRow label="Job Card" value={jc.number || jc.job_number} mono />
                <SnapRow label="Customer" value={jc.customer_snapshot?.name || "—"} />
                <SnapRow label="Vehicle" value={jc.vehicle_snapshot?.reg_number || "—"} mono />
                <SnapRow label="Status" value={String(jc.status || "").replace(/_/g, " ").toUpperCase()} />
                <div className="mt-2 rounded-md bg-[#FEF0C7] border border-[#F79009]/40 px-3 py-2 text-[11.5px] text-[#B54708]">
                  No invoice linked. Consider creating an invoice first for cleaner audit trail.
                </div>
              </div>
            ) : (
              <div className="text-[12px] text-[#667085]">Open a gate pass from an invoice or a job card.</div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

const CHECKLIST_ITEMS = [
  { key: "work_completed", label: "Work completed" },
  { key: "qc_completed", label: "Quality check completed" },
  { key: "belongings_verified", label: "Customer belongings verified" },
  { key: "old_parts_handled", label: "Old parts handled as requested" },
  { key: "invoice_issued", label: "Invoice issued" },
  { key: "payment_condition", label: "Payment condition satisfied" },
  { key: "documents_returned", label: "Vehicle documents returned" },
  { key: "keys_handed", label: "Keys handed over" },
  { key: "customer_ack", label: "Customer acknowledgement captured" },
];

function defaultForm() {
  return {
    handed_to_type: "customer",
    handed_to_name: "", handed_to_phone: "", handed_to_relation: "", handed_to_id_ref: "",
    authorised_by_customer: true,
    odometer_at_exit: "", fuel_at_exit: "", exit_at: "", security_person: "",
    customer_remarks: "", internal_notes: "",
    checklist: {}, signature_data: "",
    override_used: false, override_reason: "", override_note: "",
  };
}

function Card({ title, children, testId }) {
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid={testId}>
      <header className="px-4 py-3 border-b border-[#F1FAEE]">
        <h3 className="text-[13px] font-bold text-[#1d3557]">{title}</h3>
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function SnapRow({ label, value, mono, tone }) {
  const toneCls = tone === "green" ? "text-[#0F9256]" : tone === "red" ? "text-[#e63946]" : "text-[#1d3557]";
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
      <span className={`${mono ? "font-mono" : ""} font-semibold ${toneCls}`}>{value}</span>
    </div>
  );
}
